<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { campanhasLabel } from './campanhasLabels';

// A tabela por campanha: a campanha do Outbound (a2) e o lote (b1) juntos, uma
// linha por chave, de 25 em 25. O clique na linha abre o detalhe dela — quem
// escreve a chave no filtro é a aba. A ordem e a página ficam na aba, e não na
// URL: são desta lista.
const props = defineProps({
  // A resposta de GET campanhas/tabela; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['open']);

// Uma coluna da tabela, com `-` na frente para a decrescente; null é a ordem
// do servidor (a campanha que enviou por último primeiro).
const sort = defineModel('sort', { type: String, default: null });
const page = defineModel('page', { type: Number, default: 1 });

const { t } = useI18n();
const { number, percent, dateTime } = useAnalyticsFormat();

const COLUMNS = [
  { key: 'sent', label: 'CAMPANHAS.TABLE.SENT' },
  { key: 'delivered', label: 'CAMPANHAS.TABLE.DELIVERED' },
  { key: 'read', label: 'CAMPANHAS.TABLE.READ' },
  { key: 'replied', label: 'CAMPANHAS.TABLE.REPLIED' },
  { key: 'reply_rate', label: 'CAMPANHAS.TABLE.REPLY_RATE', percent: true },
  { key: 'failed', label: 'CAMPANHAS.TABLE.FAILED' },
  { key: 'no_confirmation', label: 'CAMPANHAS.TABLE.NO_CONFIRMATION' },
  { key: 'opted_out', label: 'CAMPANHAS.TABLE.OPTED_OUT' },
];

const timezone = computed(() => props.report?.period?.timezone);
const rows = computed(() => props.report?.rows || []);
const currentSort = computed(() => sort.value || props.report?.sort || '');
const pages = computed(() =>
  Math.max(
    Math.ceil((props.report?.total || 0) / (props.report?.per_page || 25)),
    1
  )
);

// Clicar na coluna ordena pelo maior; clicar de novo, pelo menor.
const sortBy = key => {
  sort.value = currentSort.value === `-${key}` ? key : `-${key}`;
  page.value = 1;
};
const sortState = key => {
  if (currentSort.value === `-${key}`) return 'descending';
  if (currentSort.value === key) return 'ascending';
  return 'none';
};

const cell = (row, column) => {
  const value = row[column.key];
  if (typeof value !== 'number') return t('ANALYTICS_REPORTS.NO_DATA');
  return column.percent ? percent(value) : number(value);
};

const subtitle = row =>
  [campanhasLabel(t, 'kind', row.kind), row.template, row.segment]
    .filter(Boolean)
    .join(' · ');
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanhas-tabela"
    :title="t('CAMPANHAS.TABLE.TITLE')"
    :description="t('CAMPANHAS.TABLE.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p v-else-if="report && !rows.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('CAMPANHAS.TABLE.EMPTY') }}
    </p>
    <template v-else-if="report">
      <!-- Dez colunas: a tabela rola sozinha, e não a página. -->
      <div class="overflow-x-auto max-w-full">
        <table
          class="w-full text-sm min-w-[56rem]"
          :class="{ 'opacity-60': loading }"
        >
          <thead>
            <tr class="text-xs text-n-slate-11">
              <th class="py-2 font-medium pe-3 text-start">
                {{ t('CAMPANHAS.TABLE.CAMPAIGN') }}
              </th>
              <th
                v-for="column in COLUMNS"
                :key="column.key"
                class="py-2 font-medium px-2 text-end"
                :aria-sort="sortState(column.key)"
              >
                <button
                  type="button"
                  :data-test-id="`campanhas-tabela-ordem-${column.key}`"
                  class="inline-flex gap-1 items-center hover:text-n-slate-12"
                  @click="sortBy(column.key)"
                >
                  {{ t(column.label) }}
                  <span
                    v-if="sortState(column.key) !== 'none'"
                    aria-hidden="true"
                    :class="
                      sortState(column.key) === 'descending'
                        ? 'i-lucide-arrow-down'
                        : 'i-lucide-arrow-up'
                    "
                    class="size-3"
                  />
                </button>
              </th>
              <th
                class="py-2 font-medium ps-2 text-end"
                :aria-sort="sortState('last_sent_at')"
              >
                <button
                  type="button"
                  data-test-id="campanhas-tabela-ordem-last_sent_at"
                  class="inline-flex gap-1 items-center hover:text-n-slate-12"
                  @click="sortBy('last_sent_at')"
                >
                  {{ t('CAMPANHAS.TABLE.LAST_SENT') }}
                  <span
                    v-if="sortState('last_sent_at') !== 'none'"
                    aria-hidden="true"
                    :class="
                      sortState('last_sent_at') === 'descending'
                        ? 'i-lucide-arrow-down'
                        : 'i-lucide-arrow-up'
                    "
                    class="size-3"
                  />
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="row in rows"
              :key="row.key"
              :data-test-id="`campanhas-tabela-linha-${row.key}`"
              class="cursor-pointer hover:bg-n-alpha-1"
              @click="emit('open', row.key)"
            >
              <td class="py-2 pe-3 min-w-0 max-w-[16rem]">
                <button
                  type="button"
                  class="flex flex-col items-start min-w-0 max-w-full text-start"
                  @click.stop="emit('open', row.key)"
                >
                  <span class="font-medium truncate text-n-slate-12 max-w-full">
                    {{ row.title || row.key }}
                  </span>
                  <span class="text-xs truncate text-n-slate-11 max-w-full">
                    {{ subtitle(row) }}
                  </span>
                </button>
              </td>
              <td
                v-for="column in COLUMNS"
                :key="column.key"
                class="px-2 py-2 whitespace-nowrap text-end tabular-nums text-n-slate-12"
              >
                {{ cell(row, column) }}
              </td>
              <td
                class="py-2 whitespace-nowrap ps-2 text-end tabular-nums text-n-slate-11"
              >
                {{
                  row.last_sent_at ? dateTime(row.last_sent_at, timezone) : ''
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="pages > 1"
        class="flex gap-2 justify-between items-center text-xs text-n-slate-11"
      >
        <Button
          :label="t('CAMPANHAS.TABLE.PREVIOUS')"
          xs
          slate
          faded
          type="button"
          :disabled="page <= 1 || loading"
          @click="page -= 1"
        />
        <span>
          {{ t('CAMPANHAS.TABLE.PAGE', { page, pages }) }}
        </span>
        <Button
          :label="t('CAMPANHAS.TABLE.NEXT')"
          xs
          slate
          faded
          type="button"
          :disabled="page >= pages || loading"
          @click="page += 1"
        />
      </div>
    </template>
  </AnalyticsPanel>
</template>
